import { useMemo, useRef, useState, KeyboardEvent, ChangeEvent } from 'react';
import { Box, Typography, Button as MuiButton, InputBase, Grow } from '@mui/material';
import { styled } from '@mui/material/styles';

const VerifyCodeForm = ({ onSubmit, loading, onRequestNew }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const otpRef = useRef<any>(null);

  const disabledButton = useMemo(() => {
    if (loading) return true;
    return code.join('').length < 6;
  }, [code, loading]);

  const handleSubmit = () => {
    onSubmit(code.join(''));
  };

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCode((prev) => {
      let enteredValue = [...prev];
      enteredValue[index] = value.trim();
      enteredValue = enteredValue.join('').substring(0, 6).split('');

      return prev.map(() => {
        if (enteredValue.length) {
          return enteredValue.shift() || '';
        }

        return '';
      });
    });

    // handle input focus
    if (value.length === 0) {
      setTimeout(() => {
        otpRef.current.querySelector(`[tabindex="${index}"]`)?.focus();
      }, 1);
    } else {
      const emptyVals = code.filter((val, valIndex) => valIndex < index && val === '') || [];
      setTimeout(() => {
        let endIndex = index + value.length - emptyVals.length + 1;

        // allows entering value in between
        if (code[index + 1]) {
          endIndex = endIndex - 1;
        }

        const focusIndex = endIndex < 6 ? endIndex : 6;
        otpRef.current.querySelector(`[tabindex="${focusIndex}"]`)?.focus();
      }, 1);
    }
  };

  const handleKeydown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!disabledButton) {
        handleSubmit();
      }
    }
    if (event.key === 'Backspace' || event.key === 'ArrowLeft') {
      setTimeout(() => {
        otpRef.current.querySelector(`[tabindex="${index}"]`)?.focus();
      }, 1);
    }

    if (event.key === 'ArrowRight') {
      setTimeout(() => {
        otpRef.current.querySelector(`[tabindex="${index + 2}"]`)?.setSelectionRange(-1, -1);
        otpRef.current.querySelector(`[tabindex="${index + 2}"]`)?.focus();
      }, 1);
    }
  };

  const handleBlur = () => {
    setCode((prev) => {
      const value = prev.join('').split('');
      return prev.map((_, valIndex) => value[valIndex] || '');
    });
  };

  return (
    <Grow in style={{ transformOrigin: 'top center' }}>
      <Form>
        <Title>Enter the verification code</Title>
        <Description>
          You should have received a 6-digit verification code sent to your email.
        </Description>

        <Box ref={otpRef}>
          {code.map((value, index) => (
            <OTPInput
              autoFocus={index === 0}
              key={`key-${index}`}
              inputProps={{
                autoComplete: 'off',
                tabIndex: index + 1,
              }}
              value={value}
              onChange={handleChange.bind(null, index)}
              onBlur={handleBlur}
              onKeyDown={handleKeydown.bind(null, index)}
              autoComplete="off"
            />
          ))}
        </Box>

        <Box display="flex" width="100%" flexDirection="column" mt={5} mb={2}>
          <Button fullWidth variant="contained" disabled={disabledButton} onClick={handleSubmit}>
            {loading ? 'Verifying...' : 'Submit'}
          </Button>
        </Box>
        <Typography sx={{ fontSize: 16, mb: 2 }}>
          Didn’t receive a code?{' '}
          <RequestNewCode as="span" onClick={onRequestNew} tabIndex={0}>
            Request a new code
          </RequestNewCode>
        </Typography>
      </Form>
    </Grow>
  );
};

const Form = styled('form', { label: 'Form' })(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.black,
      },
    },
  },
}));

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  color: theme.palette.custom.mirage,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
  },
}));

const Description = styled(Typography, { label: 'Description' })(({ theme }) => ({
  textAlign: 'center',
  fontSize: 18,
  margin: '24px 0',
}));

const OTPInput = styled(InputBase, { label: 'OTPInput' })(({ theme }) => ({
  width: 50,
  height: 57,
  border: `1px solid ${theme.palette.custom.line}`,
  borderRadius: 6,
  fontSize: 32,
  fontWeightL: 600,
  marginRight: 8,
  '& input': {
    textAlign: 'center',
  },
}));

const Button = styled(MuiButton, { label: 'Button' })({
  borderRadius: 8,
  height: 56,
});

const RequestNewCode = styled(Typography, { label: 'RequestNewCode' })(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.custom.black,
  fontWeight: 600,
  '&:hover, &:focus': {
    textDecoration: 'underline',
    outline: 0,
  },
}));

export default VerifyCodeForm;
